import { inject, Injectable } from "@angular/core";
import { LocalizationConfig } from "@configs/interface-management/localization.config";
import { SettingGroupConfig } from "@configs/setting/setting-group.config";
import { SettingConfig } from "@configs/setting/setting.config";
import { MeasurementUnitConfig } from "@app/shared/configs/resource-management/measurement-unit.config";
import { BaseConfig } from "@dmms/shared/configs/base/base.config";
import { MenuConfig } from "@configs/interface-management/menu.config";
import { ShiftConfig } from "@dmms/shared/configs/resource-management/shift.config";
import { DicTypeKeyEnum } from "@models/dictionary/dic-type";
import { IBaseConfig } from "@models/form/base-config";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { AuthService } from "@dmms/core/auth";
import { NotificationService } from "@dmms/core/notification";
import { DictionaryService } from "@dmms/core/http";
import { FileService } from "@dmms/shared/utils/services";
import { AppUserConfig } from "@configs/user-management/app-user.config";
import { BpUserConfig } from "@configs/user-management/bp-user.config";
import { CostCenterConfig } from "@app/shared/configs/resource-management/cost-center.config";
import { LocationConfig } from "@app/shared/configs/resource-management/location.config";
import { WorkCenterConfig } from "@app/shared/configs/resource-management/work-center.config";
import { ServiceZoneConfig } from "@app/shared/configs/resource-management/service-zone.config";
import { BusinessPartnersConfig } from "@app/shared/configs/resource-management/business-partner.config";
import { MaintenancePartnerConfig } from "@app/shared/configs/resource-management/maintenance-partner.config";
import { EquipmentBrandConfig } from "@app/shared/configs/resource-management/equipment-brand.config";
import { EquipmentModelConfig } from "@app/shared/configs/resource-management/equipment-model.config";
import { DepartmentConfig } from "@app/shared/configs/resource-management/department.config";
import { DivisionConfig } from "@app/shared/configs/resource-management/division.config";
import { GroupConfig } from "@app/shared/configs/resource-management/group.config";
import { EquipmentTypeConfig } from "@app/shared/configs/resource-management/equipment-type.config";
import { PropertyConfig } from "@app/shared/configs/resource-management/property.config";
import { FleetConfig } from "@app/shared/configs/resource-management/fleet.config";
import { EquipmentMaintenanceTypeConfig } from "@app/shared/configs/resource-management/equipment-maintenance-type.config";
import { RequestPriorityConfig } from "@app/shared/configs/resource-management/request-priority.config";
import { EquipmentBookingAuthorizationConfig } from "@configs/resource-management/equipment-booking-authorization.config";
import { DeviceConfig } from "@configs/resource-management/device.config";
import { GuideConfig } from "@configs/interface-management/guide.config";
@Injectable({
  providedIn: "root",
})
export class DictionaryFormService {
  protected readonly translate = inject(TranslateService);
  protected readonly dialog = inject(MatDialog);
  protected readonly authService = inject(AuthService);
  protected readonly notificationService = inject(NotificationService);
  protected readonly dictionaryService = inject(DictionaryService);
  protected readonly fileService = inject(FileService);

  private _filterValue?: { value: string; dicType: string };

  static configs = new Map<DicTypeKeyEnum, any>([
    [DicTypeKeyEnum.Menu, MenuConfig],
    [DicTypeKeyEnum.Shift, ShiftConfig],
    [DicTypeKeyEnum.Localization, LocalizationConfig],
    [DicTypeKeyEnum.AppUser, AppUserConfig],
    [DicTypeKeyEnum.BpUser, BpUserConfig],
    [DicTypeKeyEnum.SettingGroup, SettingGroupConfig],
    [DicTypeKeyEnum.Setting, SettingConfig],
    [DicTypeKeyEnum.MeasurementUnit, MeasurementUnitConfig],
    [DicTypeKeyEnum.BusinessPartner, BusinessPartnersConfig],
    [DicTypeKeyEnum.MaintenancePartner, MaintenancePartnerConfig],
    [DicTypeKeyEnum.EquipmentBrand, EquipmentBrandConfig],
    [DicTypeKeyEnum.EquipmentModel, EquipmentModelConfig],
    [DicTypeKeyEnum.CostCentre, CostCenterConfig],
    [DicTypeKeyEnum.Location, LocationConfig],
    [DicTypeKeyEnum.WorkCentre, WorkCenterConfig],
    [DicTypeKeyEnum.ServiceZone, ServiceZoneConfig],
    [DicTypeKeyEnum.Location, LocationConfig],
    [DicTypeKeyEnum.Department, DepartmentConfig],
    [DicTypeKeyEnum.Division, DivisionConfig],
    [DicTypeKeyEnum.Group, GroupConfig],
    [DicTypeKeyEnum.EquipmentType, EquipmentTypeConfig],
    [DicTypeKeyEnum.Property, PropertyConfig],
    [DicTypeKeyEnum.Fleet, FleetConfig],
    [DicTypeKeyEnum.EquipmentMaintenanceType, EquipmentMaintenanceTypeConfig],
    [DicTypeKeyEnum.RequestPriority, RequestPriorityConfig],
    [
      DicTypeKeyEnum.EquipmentBookingAuthorization,
      EquipmentBookingAuthorizationConfig,
    ],
    [DicTypeKeyEnum.Device, DeviceConfig],
    [DicTypeKeyEnum.Guide, GuideConfig],
  ]);

  getFilterValue(): any {
    return this._filterValue;
  }

  setFilterValue(value: any, dicType: string): void {
    this._filterValue = { value, dicType };
  }

  getConfig(dicType: DicTypeKeyEnum): IBaseConfig {
    const configClass = DictionaryFormService.configs.get(dicType);
    let config: IBaseConfig;

    if (!configClass) {
      const baseDicConfig = new BaseConfig();

      config = {
        version: 1,
        dicType,
        fields: baseDicConfig.fields,
        tableConfig: baseDicConfig.tableConfig,
      };
    } else {
      config = new configClass(
        this.translate,
        this.dialog,
        this.authService,
        this.notificationService,
        this.dictionaryService,
        this.fileService,
      );
    }

    if (!config.fields || !config?.tableConfig || !config?.filterFields) {
      const baseDicConfig = new BaseConfig();

      if (!config?.fields) {
        config.fields = baseDicConfig.fields;
      }

      if (!config?.tableConfig) {
        config.tableConfig = baseDicConfig.tableConfig;
      }

      if (!config?.filterFields) {
        config.filterFields = baseDicConfig.filterFields;
        config.filterTemplateColumns = baseDicConfig.filterTemplateColumns;
      }

      if (!config?.filterFields) {
        config.filterFields = baseDicConfig.filterFields;
      }
    }

    if (!config.tableConfig?.tableColumns) {
      config.tableConfig.tableColumns = "40px 1fr 85px";
    }

    return config;
  }
}
